import { Chip } from '@mui/material';
import { memo, ReactNode } from 'react';
import { TipoAssenza } from '../../utility_objects/AssenzeOperatoriUtils';
import { getColoriChip, StatoChip } from '../../utility_objects/PresenzeUtils';

// Una riga di testo da 16px più il bordo: alta come le chip di prima (18px).
const ALTEZZA_RIGA_TESTO = 16;
const ALTEZZA_MINIMA = ALTEZZA_RIGA_TESTO + 2;

// Trattino fra due cifre di un orario («09:00-13:00»), catturato con la cifra prima.
const TRATTINO_ORARIO = /(\d-)(?=\d)/;

/**
 * Testo della chip che va a capo, con un punto di a capo (<wbr>) dopo il trattino di
 * ogni orario. Il browser non va a capo fra trattino e cifra: senza, in una colonna
 * stretta overflow-wrap spezzerebbe l'orario a metà cifra («09:00-13:0» e «0» sotto)
 * invece che in «09:00-» e «13:00». Il testo letto resta lo stesso.
 */
const conACapoNegliOrari = (label: string): ReactNode => {
  // Con il gruppo catturato split alterna testo e «N-».
  const parti = label.split(TRATTINO_ORARIO);
  if (parti.length === 1) return label;
  const pezzi: ReactNode[] = [];
  let testo = '';
  parti.forEach((parte, indice) => {
    testo += parte;
    if (indice % 2 === 0) return;
    pezzi.push(testo, <wbr key={indice} />);
    testo = '';
  });
  pezzi.push(testo);
  return pezzi;
};

interface ChipPresenzaProps {
  label: string;
  stato: StatoChip;
  /** Tipo dell'assenza, che dà il colore; null = in servizio senza assenze. */
  tipo: TipoAssenza | null;
}

/**
 * Chip di una persona nella fascia Presenze e nel suo popover, coi colori di Turni e
 * orari (getColoriChip): in servizio senza colore (bordo e testo del tema, come
 * «Orario predefinito» nella legenda), in servizio in parte col bordo del tipo,
 * assente tutto il giorno col bordo del tipo e lo sfondo tinto. Il testo resta del
 * colore del tema, leggibile anche sul giallo; se la colonna è stretta va a capo
 * dentro la chip invece di finire con i puntini. Non è cliccabile: il clic è della
 * cella. Stesso aspetto di FOCUS CLOUD.
 */
export const ChipPresenza = memo(function ChipPresenza({
  label,
  stato,
  tipo,
}: ChipPresenzaProps): JSX.Element {
  const { bordo, sfondo } = getColoriChip(stato, tipo);
  return (
    <Chip
      size="small"
      variant="outlined"
      label={conACapoNegliOrari(label)}
      sx={{
        minWidth: 0,
        color: 'text.primary',
        borderColor: bordo,
        bgcolor: sfondo,
        height: 'auto',
        minHeight: ALTEZZA_MINIMA,
        maxWidth: '100%',
        // Metà dell'altezza di una riga: tonda su una riga, arrotondata su più righe.
        borderRadius: `${ALTEZZA_MINIMA / 2}px`,
        '& .MuiChip-label': {
          whiteSpace: 'normal',
          // Uno username più largo della colonna va a capo anche senza spazi
          // (break-word per i browser incorporati più vecchi).
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          lineHeight: `${ALTEZZA_RIGA_TESTO}px`,
          py: 0,
          px: 0.75,
        },
      }}
    />
  );
});
